import { useCallback, useRef, useEffect } from 'react';
import { getDeleteStatus } from '../services/api';
import { DELETE_POLL_INTERVAL_MS } from '../constants';

interface UseDeletePollingOptions {
  onDeleted: (siteId: string) => void;
}

/**
 * Hook that manages background polling for site deletion status.
 * Auto-starts pollers for sites already in "deleting" state.
 */
export function useDeletePolling({ onDeleted }: UseDeletePollingOptions) {
  const pollerRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const stopPoller = useCallback((siteId: string) => {
    if (pollerRefs.current[siteId]) {
      clearInterval(pollerRefs.current[siteId]);
      delete pollerRefs.current[siteId];
    }
  }, []);

  const startPoller = useCallback((siteId: string, orgId: string) => {
    // Avoid duplicates
    if (pollerRefs.current[siteId]) return;

    const poll = async () => {
      try {
        const result = await getDeleteStatus(siteId, orgId);
        if (result === null) {
          // Site fully deleted — stop polling
          stopPoller(siteId);
          onDeleted(siteId);
        }
      } catch {
        // Transient error — keep polling
      }
    };

    pollerRefs.current[siteId] = setInterval(poll, DELETE_POLL_INTERVAL_MS);
  }, [stopPoller, onDeleted]);

  // Clean up all pollers on unmount
  useEffect(() => {
    const refs = pollerRefs.current;
    return () => {
      Object.keys(refs).forEach((id) => {
        clearInterval(refs[id]);
      });
    };
  }, []);

  return { startPoller, stopPoller };
}
